import { flexRender, type Row, type Table } from "@tanstack/react-table";
import { cn } from "../ui/cn";

/**
 * The table's compact rendering.
 *
 * Rows become cards built from the *same* column definitions the desktop table
 * uses, ordered by each column's `priority`: 1 is the identifying line, 2 the
 * supporting line, 3 meta fields, and the rest stay desktop-only. A screen
 * therefore gets a usable phone layout without writing any mobile markup —
 * which is the difference between this and the empty `*MobileTemplate` stubs
 * the Vaadin app shipped.
 */
export function CardList<T>({
  table,
  onRowClick,
  rowClassName,
}: {
  table: Table<T>;
  onRowClick?: (row: T) => void;
  rowClassName?: (row: T) => string | undefined;
}) {
  const rows = table.getRowModel().rows;

  return (
    <ul className="flex flex-col gap-2 p-2">
      {rows.map((row) => (
        <li key={row.id}>
          <Card
            row={row}
            {...(onRowClick ? { onRowClick } : {})}
            {...(rowClassName ? { rowClassName } : {})}
          />
        </li>
      ))}
    </ul>
  );
}

function Card<T>({
  row,
  onRowClick,
  rowClassName,
}: {
  row: Row<T>;
  onRowClick?: (row: T) => void;
  rowClassName?: (row: T) => string | undefined;
}) {
  const cells = row.getVisibleCells();

  const byPriority = (priority: number) =>
    cells.filter((cell) => (cell.column.columnDef.meta?.priority ?? 50) === priority);

  const primary = byPriority(1);
  const secondary = byPriority(2);
  const meta = byPriority(3);
  const actions = cells.filter((cell) => cell.column.id === "actions");

  return (
    <div
      className={cn(
        "rounded-lg border border-line bg-surface p-3 shadow-card",
        onRowClick && "cursor-pointer active:bg-surface-sunk",
        row.getIsSelected() && "border-accent bg-accent-soft",
        rowClassName?.(row.original),
      )}
      onClick={onRowClick ? () => onRowClick(row.original) : undefined}
    >
      <div className="flex items-start gap-3">
        {/* Same reasoning as the grid's checkbox cell: the card navigates, so
            the checkbox needs a target big enough to hit on a phone that is not
            also a link into the record. */}
        {row.getCanSelect() && (
          <label
            className="-m-1 flex shrink-0 cursor-pointer p-1"
            onClick={(event) => event.stopPropagation()}
          >
            <input
              type="checkbox"
              checked={row.getIsSelected()}
              onChange={row.getToggleSelectedHandler()}
              aria-label="Select row"
              className="mt-1 size-4 shrink-0 accent-[var(--accent)]"
            />
          </label>
        )}

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-baseline gap-x-2 text-sm font-medium text-ink">
            {primary.map((cell) => (
              <span key={cell.id} className="truncate">
                {flexRender(cell.column.columnDef.cell, cell.getContext())}
              </span>
            ))}
          </div>

          {secondary.length > 0 && (
            <div className="mt-0.5 flex flex-wrap items-baseline gap-x-2 text-[13px] text-ink-muted">
              {secondary.map((cell) => (
                <span key={cell.id} className="truncate">
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </span>
              ))}
            </div>
          )}

          {meta.length > 0 && (
            <dl className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-[12px]">
              {meta.map((cell) => (
                <div key={cell.id} className="flex items-baseline gap-1.5">
                  <dt className="text-ink-faint">
                    {String(cell.column.columnDef.meta?.headerKey ?? cell.column.id)
                      .split(".")
                      .pop()}
                  </dt>
                  <dd className="tabular text-ink">
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </dd>
                </div>
              ))}
            </dl>
          )}
        </div>

        {actions.length > 0 && (
          <div onClick={(event) => event.stopPropagation()}>
            {actions.map((cell) => (
              <span key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
